const QUICK_VIEW_EVENT = 'bosp:candyrack-quick-view';
const CANDYRACK_ADD_EVENT = 'ppp-candyrack-button-layout-offers-added-to-cart';
const CONFIG_SELECTOR = '[data-bosp-candyrack-skin-config]';
const PRODUCT_ID_CLASS_PREFIX = 'candyrack-ppp-product-id-';
const QUICK_VIEW_IMAGE_WIDTH = 1200;
const productCache = new Map();
/**
 * @typedef {Object} AddOperation
 * @property {number} id
 * @property {HTMLElement} card
 * @property {Element | null} modalButton
 * @property {boolean} sourceClickDispatched
 */
let activeCard = null;
let activeImageIndex = 0;
let activeImages = [];
/** @type {AddOperation | null} */
let activeAddOperation = null;
let addOperationSequence = 0;
let addTimeout = 0;
let closeTimeout = 0;
let previousBodyOverflow = '';

function getConfig() {
  const source = document.querySelector(CONFIG_SELECTOR);

  return {
    storefrontAccessToken: source?.dataset.storefrontAccessToken || '',
    storefrontApiVersion: source?.dataset.storefrontApiVersion || '',
    storefrontLanguageCode: source?.dataset.storefrontLanguageCode || 'EN',
    specialOffer: source?.dataset.specialOfferLabel || '',
    previous: source?.dataset.previousLabel || '',
    next: source?.dataset.nextLabel || '',
    eyebrow: source?.dataset.quickViewEyebrow || '',
    discountContext: source?.dataset.quickViewDiscountContext || '',
    variantLabel: source?.dataset.quickViewVariantLabel || '',
    addLabel: source?.dataset.quickViewAddLabel || '',
    addedLabel: source?.dataset.quickViewAddedLabel || '',
    closeLabel: source?.dataset.quickViewCloseLabel || '',
    stockLabel: source?.dataset.quickViewStockLabel || '',
    returnsLabel: source?.dataset.quickViewReturnsLabel || '',
    loadingLabel: source?.dataset.quickViewLoadingLabel || '',
    errorLabel: source?.dataset.quickViewErrorLabel || '',
    galleryLabel: source?.dataset.quickViewGalleryLabel || '',
    imageStatusTemplate: source?.dataset.quickViewImageStatusTemplate || '',
    regularPriceLabel: source?.dataset.quickViewRegularPriceLabel || '',
    salePriceLabel: source?.dataset.quickViewSalePriceLabel || '',
  };
}

function getProductId(card) {
  const className = [...card.classList].find((value) => value.startsWith(PRODUCT_ID_CLASS_PREFIX));
  return className?.slice(PRODUCT_ID_CLASS_PREFIX.length) || '';
}

function getMetaobjectField(fields, key) {
  return fields?.find((field) => field.key === key) || null;
}

function getFeatureData(productFeatures) {
  const fields = productFeatures?.reference?.fields || [];
  const features = [];

  for (let index = 1; index <= 4; index += 1) {
    const featureFields = getMetaobjectField(fields, `product_feature_${index}`)?.reference?.fields || [];
    const title = getMetaobjectField(featureFields, 'title')?.value?.trim() || '';
    const description = getMetaobjectField(featureFields, 'description')?.value?.trim() || '';

    if (title || description) features.push({ title, description });
  }

  return {
    claim: getMetaobjectField(fields, 'claim')?.value?.trim() || '',
    features,
  };
}

async function fetchProduct(productId) {
  if (productCache.has(productId)) return productCache.get(productId);

  const config = getConfig();
  if (!config.storefrontAccessToken || !config.storefrontApiVersion) {
    throw new Error('CandyRack quick-view Storefront API configuration is missing.');
  }

  const request = fetch(`/api/${config.storefrontApiVersion}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
    },
    body: JSON.stringify({
      query: `
        query CrossSellQuickView($id: ID!, $language: LanguageCode!) @inContext(language: $language) {
          node(id: $id) {
            ... on Product {
              id
              title
              description
              images(first: 8) {
                nodes {
                  url
                  altText
                }
              }
              productFeatures: metafield(namespace: "custom", key: "product_features") {
                reference {
                  ... on Metaobject {
                    fields {
                      key
                      type
                      value
                      reference {
                        ... on Metaobject {
                          fields {
                            key
                            type
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        id: `gid://shopify/Product/${productId}`,
        language: config.storefrontLanguageCode,
      },
    }),
  }).then(async (response) => {
    if (!response.ok) throw new Error(`Storefront API request failed with status ${response.status}`);

    const result = await response.json();
    if (result.errors) throw new Error(result.errors.map((error) => error.message).join(', '));
    if (!result.data?.node) throw new Error('Cross-sell product was not returned by the Storefront API.');

    const featureData = getFeatureData(result.data.node.productFeatures);
    return {
      title: result.data.node.title || '',
      description: result.data.node.description || '',
      images: (result.data.node.images?.nodes || []).filter((image) => image?.url),
      claim: featureData.claim,
      features: featureData.features,
    };
  });

  productCache.set(productId, request);

  try {
    return await request;
  } catch (error) {
    productCache.delete(productId);
    throw error;
  }
}

function closeIcon() {
  return `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    </svg>
  `;
}

function arrowIcon(direction) {
  const path = direction === 'previous' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6';
  return `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="${path}" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
}

function checkIcon() {
  return `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4.5 12.5l5 5 10-11"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  `;
}

function createCheckIcon() {
  const namespace = 'http://www.w3.org/2000/svg';
  const icon = document.createElementNS(namespace, 'svg');
  const path = document.createElementNS(namespace, 'path');

  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('aria-hidden', 'true');
  path.setAttribute('d', 'M4.5 12.5l5 5 10-11');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.7');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  icon.append(path);

  return icon;
}

function returnIcon() {
  return `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  `;
}

function ensureDialog() {
  let dialog = document.querySelector('.bosp-cross-sell-quick-view');
  if (dialog instanceof HTMLDialogElement) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'bosp-cross-sell-quick-view';
  dialog.setAttribute('aria-labelledby', 'bosp-cross-sell-quick-view-title');
  dialog.innerHTML = `
    <div class="bosp-cross-sell-quick-view__shell">
      <div class="bosp-cross-sell-quick-view__handle" aria-hidden="true"><span></span></div>
      <button class="bosp-cross-sell-quick-view__close" type="button">
        ${closeIcon()}
      </button>
      <div class="bosp-cross-sell-quick-view__media" role="region" aria-roledescription="carousel">
        <span class="bosp-cross-sell-quick-view__pill"></span>
        <img class="bosp-cross-sell-quick-view__image" alt="">
        <div class="bosp-cross-sell-quick-view__gallery-nav">
          <button type="button" data-quick-view-image="previous">
            ${arrowIcon('previous')}
          </button>
          <button type="button" data-quick-view-image="next">${arrowIcon('next')}</button>
        </div>
        <div class="bosp-cross-sell-quick-view__dots" aria-hidden="true"></div>
        <p class="bosp-cross-sell-quick-view__image-status visually-hidden" aria-live="polite" aria-atomic="true"></p>
      </div>
      <div class="bosp-cross-sell-quick-view__body">
        <div class="bosp-cross-sell-quick-view__content">
          <div class="bosp-cross-sell-quick-view__eyebrow">
            <span></span>
            <strong class="bosp-cross-sell-quick-view__discount"></strong>
          </div>
          <h2 id="bosp-cross-sell-quick-view-title" class="bosp-cross-sell-quick-view__title"></h2>
          <p class="bosp-cross-sell-quick-view__claim"></p>
          <p class="bosp-cross-sell-quick-view__description"></p>
          <div class="bosp-cross-sell-quick-view__price">
            <strong class="bosp-cross-sell-quick-view__sale-price">
              <span class="visually-hidden" data-quick-view-label="sale-price"></span>
              <span data-quick-view-price="sale"></span>
            </strong>
            <s class="bosp-cross-sell-quick-view__regular-price">
              <span class="visually-hidden" data-quick-view-label="regular-price"></span>
              <span data-quick-view-price="regular"></span>
            </s>
            <span></span>
          </div>
          <div class="bosp-cross-sell-quick-view__variants"></div>
          <div class="bosp-cross-sell-quick-view__features"></div>
          <p class="bosp-cross-sell-quick-view__status" role="status"></p>
        </div>
        <div class="bosp-cross-sell-quick-view__footer">
          <button class="bosp-cross-sell-quick-view__add" type="button"></button>
          <div class="bosp-cross-sell-quick-view__trust">
            <span>${checkIcon()}<span data-quick-view-label="stock"></span></span>
            <span>${returnIcon()}<span data-quick-view-label="returns"></span></span>
          </div>
        </div>
      </div>
    </div>
  `;

  const config = getConfig();
  const closeButton = dialog.querySelector('.bosp-cross-sell-quick-view__close');
  const previousButton = dialog.querySelector('[data-quick-view-image="previous"]');
  const nextButton = dialog.querySelector('[data-quick-view-image="next"]');
  const addButton = dialog.querySelector('.bosp-cross-sell-quick-view__add');
  const media = dialog.querySelector('.bosp-cross-sell-quick-view__media');
  closeButton?.setAttribute('aria-label', config.closeLabel);
  previousButton?.setAttribute('aria-label', config.previous);
  nextButton?.setAttribute('aria-label', config.next);
  media?.setAttribute('aria-label', config.galleryLabel);
  dialog.querySelector('.bosp-cross-sell-quick-view__eyebrow > span').textContent = config.eyebrow;
  dialog.querySelector('.bosp-cross-sell-quick-view__price > span').textContent = config.discountContext;
  dialog.querySelector('[data-quick-view-label="stock"]').textContent = config.stockLabel;
  dialog.querySelector('[data-quick-view-label="returns"]').textContent = config.returnsLabel;
  dialog.querySelector('[data-quick-view-label="sale-price"]').textContent = `${config.salePriceLabel}: `;
  dialog.querySelector('[data-quick-view-label="regular-price"]').textContent = `${config.regularPriceLabel}: `;
  if (addButton instanceof HTMLButtonElement) addButton.textContent = config.addLabel;

  document.body.append(dialog);

  dialog.querySelector('.bosp-cross-sell-quick-view__close')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => {
    activeCard = null;
    if (activeAddOperation && !activeAddOperation.sourceClickDispatched) clearAddOperation(activeAddOperation);
    window.clearTimeout(closeTimeout);
    closeTimeout = 0;
    document.body.style.overflow = previousBodyOverflow;
  });
  dialog.querySelector('.bosp-cross-sell-quick-view__add')?.addEventListener('click', addActiveOffer);

  for (const button of dialog.querySelectorAll('[data-quick-view-image]')) {
    button.addEventListener('click', () => {
      const direction = button.getAttribute('data-quick-view-image') === 'previous' ? -1 : 1;
      showImage(activeImageIndex + direction);
    });
  }

  dialog.querySelector('.bosp-cross-sell-quick-view__content')?.addEventListener('scroll', updateScrollFade, {
    passive: true,
  });

  return dialog;
}

function getCardText(card, selector) {
  return card.querySelector(selector)?.textContent?.trim() || '';
}

function parsePrice(value) {
  const digits = value.replace(/[^\d]/g, '');
  return digits ? Number(digits) : 0;
}

function getCurrencySymbol(currencyCode) {
  try {
    const locale = document.documentElement.lang || navigator.language;
    const currencyPart = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency');

    return currencyPart?.value || currencyCode;
  } catch (_error) {
    return currencyCode;
  }
}

function formatPrice(value) {
  return value.replace(/\b[A-Z]{3}\b/g, (currencyCode) => getCurrencySymbol(currencyCode));
}

function getDiscountLabel(regularPrice, salePrice) {
  const regular = parsePrice(regularPrice);
  const sale = parsePrice(salePrice);
  if (!regular || sale >= regular) return '';
  return `−${Math.round((1 - sale / regular) * 100)} %`;
}

function isHeroCard(card) {
  return card.parentElement?.querySelector('.candyrack-ppp-button-layout__offer') === card;
}

function setHidden(element, hidden) {
  if (element instanceof HTMLElement) element.hidden = hidden;
}

function getImageIdentity(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    const path = decodeURIComponent(url.pathname).replace(
      /_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|x\d+)(?:@\d+x)?(?=\.[^.]+$)/i,
      '',
    );

    return `${url.hostname}${path}`;
  } catch (_error) {
    return value.split('?')[0];
  }
}

function getHighResolutionImageUrl(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    url.pathname = url.pathname.replace(
      /_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|x\d+)(?:@\d+x)?(?=\.[^.]+$)/i,
      '',
    );
    url.searchParams.set('width', String(QUICK_VIEW_IMAGE_WIDTH));
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.href;
  } catch (_error) {
    return value;
  }
}

function showImage(index) {
  const dialog = ensureDialog();
  const image = dialog.querySelector('.bosp-cross-sell-quick-view__image');
  const status = dialog.querySelector('.bosp-cross-sell-quick-view__image-status');
  if (!(image instanceof HTMLImageElement)) return;

  if (!activeImages.length) {
    activeImageIndex = 0;
    image.removeAttribute('src');
    image.alt = '';
    image.hidden = true;
    if (status instanceof HTMLElement) status.textContent = '';
    return;
  }

  activeImageIndex = (index + activeImages.length) % activeImages.length;
  const currentImage = activeImages[activeImageIndex];
  image.hidden = false;
  image.src = currentImage.url;
  image.alt = currentImage.altText || '';

  if (status instanceof HTMLElement) {
    status.textContent = getConfig()
      .imageStatusTemplate.replace('[index]', String(activeImageIndex + 1))
      .replace('[length]', String(activeImages.length));
  }

  for (const [dotIndex, dot] of [...dialog.querySelectorAll('.bosp-cross-sell-quick-view__dots span')].entries()) {
    dot.classList.toggle('is-active', dotIndex === activeImageIndex);
  }
}

function renderImages(images, fallbackImage) {
  const dialog = ensureDialog();
  activeImages = images.length ? images : fallbackImage?.url ? [fallbackImage] : [];
  activeImageIndex = 0;

  const nav = dialog.querySelector('.bosp-cross-sell-quick-view__gallery-nav');
  const dots = dialog.querySelector('.bosp-cross-sell-quick-view__dots');
  setHidden(nav, activeImages.length <= 1);

  if (dots instanceof HTMLElement) {
    dots.replaceChildren(...activeImages.map(() => document.createElement('span')));
    dots.hidden = activeImages.length <= 1;
  }

  showImage(0);
}

function renderVariants(card, focusTarget = null) {
  const dialog = ensureDialog();
  const container = dialog.querySelector('.bosp-cross-sell-quick-view__variants');
  if (!(container instanceof HTMLElement)) return;

  container.replaceChildren();
  const sourceSelects = [...card.querySelectorAll('select')];

  for (const [selectIndex, sourceSelect] of sourceSelects.entries()) {
    const options = [...sourceSelect.options];
    if (options.length <= 1 && options[0]?.text.trim().toLowerCase().includes('default')) continue;

    const group = document.createElement('fieldset');
    group.className = 'bosp-cross-sell-quick-view__variant';
    const legend = document.createElement('legend');
    legend.textContent = getConfig().variantLabel;
    group.append(legend);

    const choices = document.createElement('div');
    choices.className = 'bosp-cross-sell-quick-view__variant-options';

    for (const option of options) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bosp-cross-sell-quick-view__variant-option';
      button.textContent = option.text.trim();
      button.dataset.selectIndex = String(selectIndex);
      button.dataset.value = option.value;
      button.disabled = option.disabled;
      button.classList.toggle('is-selected', option.value === sourceSelect.value);
      button.setAttribute('aria-pressed', option.value === sourceSelect.value ? 'true' : 'false');
      button.addEventListener('click', () => {
        for (const sibling of choices.querySelectorAll('.bosp-cross-sell-quick-view__variant-option')) {
          sibling.classList.toggle('is-selected', sibling === button);
          sibling.setAttribute('aria-pressed', sibling === button ? 'true' : 'false');
        }

        setSourceSelectValue(sourceSelect, option.value);
        requestAnimationFrame(() => {
          requestAnimationFrame(() =>
            refreshVariantPresentation(card, { selectIndex, value: option.value }),
          );
        });
      });
      choices.append(button);
    }

    group.append(choices);
    container.append(group);
  }

  container.hidden = container.childElementCount === 0;

  if (focusTarget) {
    const replacement = [...container.querySelectorAll('.bosp-cross-sell-quick-view__variant-option')].find(
      (button) =>
        button instanceof HTMLButtonElement &&
        button.dataset.selectIndex === String(focusTarget.selectIndex) &&
        button.dataset.value === focusTarget.value,
    );
    if (replacement instanceof HTMLButtonElement) replacement.focus();
  }
}

function setAddState(added) {
  const dialog = ensureDialog();
  const button = dialog.querySelector('.bosp-cross-sell-quick-view__add');
  if (!(button instanceof HTMLButtonElement)) return;

  const config = getConfig();
  button.classList.toggle('is-added', added);
  button.disabled = added;
  button.setAttribute('aria-label', added ? config.addedLabel : config.addLabel);
  button.replaceChildren();
  if (added) {
    button.append(createCheckIcon());
  } else {
    button.textContent = config.addLabel;
  }
}

function updatePricing(card) {
  const dialog = ensureDialog();
  const regularPrice = getCardText(card, '.candyrack-ppp-button-layout__info--product-price');
  const salePrice = getCardText(card, '.candyrack-ppp-button-layout__info--product-discount-price');

  dialog.querySelector('[data-quick-view-price="regular"]').textContent = formatPrice(regularPrice);
  dialog.querySelector('[data-quick-view-price="sale"]').textContent = formatPrice(salePrice);
  dialog.querySelector('.bosp-cross-sell-quick-view__discount').textContent = getDiscountLabel(regularPrice, salePrice);
}

function refreshVariantPresentation(card, focusTarget = null) {
  if (activeCard !== card) return;

  updatePricing(card);
  renderVariants(card, focusTarget);

  const sourceImage = card.querySelector('.candyrack-ppp-image');
  if (sourceImage instanceof HTMLImageElement) {
    const sourceUrl = sourceImage.currentSrc || sourceImage.src;
    const sourceIdentity = getImageIdentity(sourceUrl);
    const matchingImageIndex = activeImages.findIndex(
      (image) => getImageIdentity(image.url) === sourceIdentity,
    );

    if (matchingImageIndex >= 0) {
      showImage(matchingImageIndex);
    } else if (sourceUrl) {
      activeImages[0] = {
        url: getHighResolutionImageUrl(sourceUrl),
        altText: sourceImage.alt,
      };
      showImage(0);
    }
  }
}

function updateScrollFade() {
  const dialog = document.querySelector('.bosp-cross-sell-quick-view');
  const content = dialog?.querySelector('.bosp-cross-sell-quick-view__content');
  if (!(dialog instanceof HTMLDialogElement) || !(content instanceof HTMLElement)) return;

  const hasMore = content.scrollHeight - content.clientHeight - content.scrollTop > 4;
  dialog.classList.toggle('has-more', hasMore);
}

function renderLoading(card) {
  const dialog = ensureDialog();
  const config = getConfig();
  const title = getCardText(card, '.candyrack-ppp-button-layout__info--product-name');
  const sourceImage = card.querySelector('.candyrack-ppp-image');
  const fallbackImage =
    sourceImage instanceof HTMLImageElement
      ? {
          url: getHighResolutionImageUrl(sourceImage.currentSrc || sourceImage.src),
          altText: sourceImage.alt || title,
        }
      : null;

  dialog.querySelector('.bosp-cross-sell-quick-view__title').textContent = title;
  updatePricing(card);
  dialog.querySelector('.bosp-cross-sell-quick-view__status').textContent = config.loadingLabel;
  dialog.querySelector('.bosp-cross-sell-quick-view__claim').textContent = '';
  dialog.querySelector('.bosp-cross-sell-quick-view__description').textContent = '';
  dialog.querySelector('.bosp-cross-sell-quick-view__features').replaceChildren();
  setHidden(dialog.querySelector('.bosp-cross-sell-quick-view__claim'), true);
  setHidden(dialog.querySelector('.bosp-cross-sell-quick-view__description'), true);
  setHidden(dialog.querySelector('.bosp-cross-sell-quick-view__features'), true);

  const pill = dialog.querySelector('.bosp-cross-sell-quick-view__pill');
  pill.textContent = config.specialOffer;
  setHidden(pill, !isHeroCard(card) || !config.specialOffer);

  renderImages([], fallbackImage);
  renderVariants(card);
  setAddState(card.classList.contains('bosp-cross-sell__offer--at-limit'));
}

function renderProduct(card, product) {
  const dialog = ensureDialog();
  const sourceImage = card.querySelector('.candyrack-ppp-image');
  const fallbackImage =
    sourceImage instanceof HTMLImageElement
      ? {
          url: getHighResolutionImageUrl(sourceImage.currentSrc || sourceImage.src),
          altText: sourceImage.alt || product.title,
        }
      : null;
  const title = dialog.querySelector('.bosp-cross-sell-quick-view__title');
  const claim = dialog.querySelector('.bosp-cross-sell-quick-view__claim');
  const description = dialog.querySelector('.bosp-cross-sell-quick-view__description');
  const features = dialog.querySelector('.bosp-cross-sell-quick-view__features');

  title.textContent = product.title || title.textContent;
  claim.textContent = product.claim;
  description.textContent = product.description;
  setHidden(claim, !product.claim);
  setHidden(description, !product.description);

  if (features instanceof HTMLElement) {
    features.replaceChildren();
    for (const feature of product.features) {
      const item = document.createElement('article');
      const itemTitle = document.createElement('strong');
      const itemDescription = document.createElement('span');
      itemTitle.textContent = feature.title;
      itemDescription.textContent = feature.description;
      item.append(itemTitle, itemDescription);
      features.append(item);
    }
    features.hidden = features.childElementCount === 0;
  }

  renderImages(product.images, fallbackImage);
  dialog.querySelector('.bosp-cross-sell-quick-view__status').textContent = '';
  requestAnimationFrame(updateScrollFade);
}

async function openQuickView(card) {
  const productId = getProductId(card);
  if (!productId) return;

  const dialog = ensureDialog();
  if (activeAddOperation && !activeAddOperation.sourceClickDispatched) clearAddOperation(activeAddOperation);
  activeCard = card;
  window.clearTimeout(closeTimeout);
  closeTimeout = 0;
  renderLoading(card);
  if (activeAddOperation) {
    const addButton = dialog.querySelector('.bosp-cross-sell-quick-view__add');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;
  }

  if (!dialog.open) {
    previousBodyOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  dialog.querySelector('.bosp-cross-sell-quick-view__close')?.focus();
  requestAnimationFrame(updateScrollFade);

  try {
    const product = await fetchProduct(productId);
    if (activeCard !== card || !dialog.open) return;
    renderProduct(card, product);
  } catch (error) {
    console.error('Unable to load CandyRack quick-view product data.', error);
    if (activeCard !== card || !dialog.open) return;
    dialog.querySelector('.bosp-cross-sell-quick-view__status').textContent = getConfig().errorLabel;
    requestAnimationFrame(updateScrollFade);
  }
}

function setSourceSelectValue(sourceSelect, value) {
  if (!(sourceSelect instanceof HTMLSelectElement) || sourceSelect.value === value) return;

  const valueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
  if (valueSetter) {
    valueSetter.call(sourceSelect, value);
  } else {
    sourceSelect.value = value;
  }
  sourceSelect.dispatchEvent(new Event('input', { bubbles: true }));
  sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
}

function syncVariantSelections(card) {
  const dialog = ensureDialog();
  const sourceSelects = [...card.querySelectorAll('select')];

  for (const selectedButton of dialog.querySelectorAll('.bosp-cross-sell-quick-view__variant-option.is-selected')) {
    const selectIndex = Number(selectedButton.getAttribute('data-select-index'));
    const value = selectedButton.getAttribute('data-value');
    const sourceSelect = sourceSelects[selectIndex];
    if (!(sourceSelect instanceof HTMLSelectElement) || value === null) continue;

    setSourceSelectValue(sourceSelect, value);
  }
}

async function addActiveOffer() {
  if (!(activeCard instanceof HTMLElement) || activeAddOperation) return;
  if (activeCard.classList.contains('bosp-cross-sell__offer--at-limit')) {
    setAddState(true);
    return;
  }

  const sourceButton = activeCard.querySelector('.candyrack-ppp-button');
  if (!(sourceButton instanceof HTMLButtonElement) || sourceButton.disabled) return;

  const operationCard = activeCard;
  syncVariantSelections(operationCard);
  const modalButton = ensureDialog().querySelector('.bosp-cross-sell-quick-view__add');
  if (modalButton instanceof HTMLButtonElement) modalButton.disabled = true;

  const operation = {
    id: ++addOperationSequence,
    card: operationCard,
    modalButton,
    sourceClickDispatched: false,
  };
  activeAddOperation = operation;

  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  if (activeAddOperation !== operation || activeCard !== operationCard || !ensureDialog().open) {
    clearAddOperation(operation);
    return;
  }

  operation.sourceClickDispatched = true;
  sourceButton.click();

  addTimeout = window.setTimeout(() => {
    if (!clearAddOperation(operation)) return;
    if (modalButton instanceof HTMLButtonElement && modalButton.isConnected) modalButton.disabled = false;
  }, 6000);
}

function clearAddOperation(operation) {
  if (activeAddOperation !== operation) return false;

  activeAddOperation = null;
  window.clearTimeout(addTimeout);
  addTimeout = 0;
  return true;
}

document.addEventListener(QUICK_VIEW_EVENT, (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('.candyrack-ppp-button-layout__offer');
  if (!(card instanceof HTMLElement)) return;
  if (!getProductId(card)) return;
  event.preventDefault();
  void openQuickView(card);
});

document.addEventListener('pointerover', (event) => {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('.candyrack-ppp-button-layout__offer');
  if (!(card instanceof HTMLElement)) return;
  const productId = getProductId(card);
  if (productId && !productCache.has(productId)) void fetchProduct(productId).catch(() => {});
});

document.addEventListener(CANDYRACK_ADD_EVENT, () => {
  const operation = activeAddOperation;
  if (!operation || !operation.sourceClickDispatched || !clearAddOperation(operation)) return;

  const dialog = ensureDialog();
  if (activeCard !== operation.card || !dialog.open) {
    if (dialog.open && activeCard instanceof HTMLElement) {
      setAddState(activeCard.classList.contains('bosp-cross-sell__offer--at-limit'));
    }
    return;
  }
  setAddState(true);

  window.clearTimeout(closeTimeout);
  closeTimeout = window.setTimeout(() => {
    closeTimeout = 0;
    if (dialog.open) dialog.close();
  }, 700);
});

window.addEventListener('resize', updateScrollFade, { passive: true });
